import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from 'prop-types';
import { Search, FileText, Upload, ChevronDown, ChevronUp } from "lucide-react";
import { getRiskColor, getStatusText, getStatusColor, getRiskStatusBadge, getRiskText, getRiskTextColor } from "../../../../utils/index";

// Constants for session findings labels
const SESSION_FINDINGS_LABELS = {
  selfHarmSymptoms: 'Self Harm Symptoms',
  depressionSymptoms: 'Depression Symptoms',
  anxietySymptoms: 'Anxiety Symptoms',
  angerIssues: 'Anger Issues'
};

// Helper function to format symptom levels
const formatSymptomLevel = (level) => {
  if (!level || level === "None") return "None";
  if (typeof level !== 'string') return "None";
  return level.charAt(0).toUpperCase() + level.toLowerCase().slice(1);
};

// Helper function to safely parse dates
const parseDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

// Helper function to safely parse time
const parseTime = (timeString) => {
  if (!timeString || typeof timeString !== 'string') return null;
  const timeParts = timeString.split(":");
  if (timeParts.length !== 2) return null;
  const [hours, minutes] = timeParts;
  if (isNaN(hours) || isNaN(minutes)) return null;
  return { hours: parseInt(hours, 10), minutes: parseInt(minutes, 10) };
};

function SessionHistory({ sessions = [], onExport, onSessionUpdated, appointmentId }) {
  const [sessionData, setSessionData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  // Memoized processed sessions with error handling
  const processedSessions = useMemo(() => {
    if (!Array.isArray(sessions) || sessions.length === 0) {
      return [];
    }

    try {
      return sessions
        .filter(session => session && typeof session === 'object') // Filter out invalid sessions
        .map((session, index) => {
          const safeSession = {
            appointmentId: session.appointmentId || '',
            date: session.date || '',
            startTime: session.startTime || '',
            practitioner: session.practitioner || 'Unknown',
            status: session.status || 'PENDING',
            mode: session.mode || 'Audio',
            riskStatus: session.riskStatus,
            prescriptionUrl: session.prescriptionUrl || null,
            notesUrl: session.notesUrl || null,
            ...session
          };

          return {
            ...safeSession,
            expanded: index === 0, // First session expanded by default
            uniqueId: `${safeSession.appointmentId || 'unknown'}-${safeSession.date || 'no-date'}-${index}`,
            sessionFindings: {
              selfHarmSymptoms: formatSymptomLevel(session.selfHarmSymptoms),
              depressionSymptoms: formatSymptomLevel(session.depressionSymptoms), 
              anxietySymptoms: formatSymptomLevel(session.anxietySymptoms),
              angerIssues: formatSymptomLevel(session.angerIssues)
            },
            recommendations: {
              assessments: session.assessments || "N/A",
              exercises: session.exercises || "N/A",
              clinicalConsultation: session.clinicalConsultation ? "Yes" : "No",
              medicalConsultation: session.medicalConsultation ? "Yes" : "No"
            }
          };
        })
        .sort((a, b) => {
          // First compare by date
          const dateA = parseDate(a.date);
          const dateB = parseDate(b.date);
          
          if (dateA && dateB && dateA.getTime() !== dateB.getTime()) {
            return dateB.getTime() - dateA.getTime(); // Most recent first
          }
          
          // If dates are equal or invalid, compare by start time
          const timeA = parseTime(a.startTime);
          const timeB = parseTime(b.startTime);
          
          if (timeA && timeB) {
            const timeAMinutes = timeA.hours * 60 + timeA.minutes;
            const timeBMinutes = timeB.hours * 60 + timeB.minutes;
            return timeBMinutes - timeAMinutes; // Most recent time first
          }
          
          return 0;
        })
        .map((session, index) => ({
          ...session,
          expanded: index === 0
        }));
    } catch (error) {
      console.error('Error processing sessions:', error);
      return [];
    }
  }, [sessions]);

  // Update session data when processed sessions change
  useEffect(() => {
    setSessionData(processedSessions);
  }, [processedSessions]);

  // Memoized filtered session data
  const filteredSessionData = useMemo(() => {
    if (!searchQuery.trim()) {
      return sessionData;
    }

    const query = searchQuery.toLowerCase().trim();
    return sessionData.filter((session) => {
      try {
        const appointmentIdMatch = session.appointmentId &&
          typeof session.appointmentId === 'string' &&
          session.appointmentId.toLowerCase().includes(query);

        const practitionerNameMatch = session.practitioner &&
          typeof session.practitioner === 'string' &&
          session.practitioner.toLowerCase().includes(query);

        // Also search through counsellor first and last name fields
        const counsellorFirstNameMatch = session.counsellorFirstName &&
          typeof session.counsellorFirstName === 'string' &&
          session.counsellorFirstName.toLowerCase().includes(query);

        const counsellorLastNameMatch = session.counsellorLastName &&
          typeof session.counsellorLastName === 'string' &&
          session.counsellorLastName.toLowerCase().includes(query);

        // Search through the full name combination
        const fullCounsellorName = session.counsellorFirstName && session.counsellorLastName
          ? `${session.counsellorFirstName} ${session.counsellorLastName}`.toLowerCase()
          : '';
        const fullNameMatch = fullCounsellorName && fullCounsellorName.includes(query);

        return appointmentIdMatch || practitionerNameMatch || counsellorFirstNameMatch || counsellorLastNameMatch || fullNameMatch;
      } catch (error) {
        console.error('Error filtering session:', session, error);
        return false;
      }
    });
  }, [sessionData, searchQuery]);

  // Memoized toggle session function
  const toggleSession = useCallback((uniqueId) => {
    if (!uniqueId) return;
    
    setSessionData(prev => 
      prev.map(session => 
        session.uniqueId === uniqueId
          ? { ...session, expanded: !session.expanded }
          : session
      )
    );
  }, []);

  // Memoized risk badge style function - use utility functions for consistency
  const getRiskBadgeStyle = useCallback((riskStatus) => {
    return getRiskStatusBadge(riskStatus);
  }, []);

  // Memoized time formatting function
  const formatTime = useCallback((timeString) => {
    const time = parseTime(timeString);
    if (!time) return "";
    
    try {
      const date = new Date();
      date.setHours(time.hours, time.minutes);

      return date.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      console.error('Error formatting time:', timeString, error);
      return "";
    }
  }, []);

  // Memoized date formatting function
  const formatApiDate = useCallback((session) => {
    if (!session || typeof session !== 'object') return "";
    
    try {
      let dateStr = session.date || "";
      if (dateStr && typeof dateStr === 'string') {
        // Convert from yyyy-mm-dd to dd-mm-yyyy format
        const dateParts = dateStr.split('-');
        if (dateParts.length === 3 && dateParts.every(part => !isNaN(part))) {
          dateStr = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
        }
      }
      
      if (session.startTime) {
        const formattedTime = formatTime(session.startTime);
        if (formattedTime) {
          // If date already has time, use as is. If not, append it.
          if (dateStr.includes(':') || dateStr.toLowerCase().includes('am') || dateStr.toLowerCase().includes('pm')) {
            return dateStr;
          } else {
            return `${dateStr} ${formattedTime}`;
          }
        }
      }
      return dateStr;
    } catch (error) {
      console.error('Error formatting date:', session, error);
      return "";
    }
  }, [formatTime]);

  // Memoized search handler
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);
  }, []);

  // Memoized export handler
  const handleExport = useCallback(() => {
    try {
      if (onExport && typeof onExport === 'function') {
        // Use the export handler passed from parent component
        onExport();
      } else {
        // Fallback to basic export if no handler provided
        const exportId = appointmentId || 'Phy001';
        window.open(`/member-summary-report/${exportId}`, '_blank');
      }
    } catch (error) {
      console.error('Error opening export window:', error);
    }
  }, [onExport, appointmentId]);

  // Toggle function for expanding/collapsing the entire section
  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  return (
    <div className="w-full">
      {/* Session History Section */}
      <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl shadow-sm p-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4">
          <div className="flex items-center gap-4">
            <h2 className="text-sm md:text-[16px] font-semibold text-[#171717] font-poppins">
              Session History
            </h2>
            {/* Expand/Collapse Button */}
            <button
              onClick={toggleExpanded}
              className="flex items-center gap-2 text-sm font-semibold font-poppins text-[#2563EB] hover:opacity-80 transition-colors bg-transparent border-none p-0"
              title={isExpanded ? "Collapse Session History" : "Expand Session History"}
            >
              {isExpanded ? (
                <>
                  <span>Hide</span>
                  <ChevronUp className="h-4 w-4" />
                </>
              ) : (
                <>
                  <span>Click to view</span>
                  <ChevronDown className="h-4 w-4" />
                </>
              )}
            </button>
            {/* Export Button - Only show when expanded */}
            {/*{isExpanded && (*/}
            {/*  <button*/}
            {/*    className="bg-[#FFFFFF] border border-[#B3C8E1] rounded-lg p-1.5 hover:bg-gray-50 transition-colors"*/}
            {/*    onClick={handleExport}*/}
            {/*    title="Export Session History"*/}
            {/*  >*/}
            {/*    <Upload className="h-4 w-4 text-[#4B61FF]" />*/}
            {/*  </button>*/}
            {/*)}*/}
          </div>
          {/* Search Box - Only show when expanded */}
          {isExpanded && (
            <div className="bg-white box-border flex items-center gap-2 pl-4 pr-4 sm:pr-16 py-2 relative rounded-xl shadow-sm w-full sm:w-[364px] h-9 border-[1px] border-solid border-[#E2E8F0]">
              <Search className="size-4 text-[#94A3B8] shrink-0" />
              <input
                type="text"
                placeholder="Search by Apt id, Practitioner name"
                value={searchQuery}
                onChange={handleSearchChange}
                className="flex-1 bg-transparent border-none outline-none font-['Poppins'] text-sm text-[#171717] placeholder-[#94A3B8] tracking-[0.28px] leading-[normal]"
              />
            </div>
          )}
        </div>

        {/* Timeline - Only show when expanded */}
        {isExpanded && (
          <div className="relative pl-4 sm:pl-6">
            {/* Vertical line */}
            <div className="absolute left-[7px] sm:left-[7px] top-2 bottom-0 w-[2px] bg-[#4B61FF]"></div>

            {/* Timeline items */}
            <div className="space-y-8">
              {filteredSessionData.length > 0 ? (
              filteredSessionData.map((session, index) => {
              // Calculate chronological session number based on original sessionData position
              const originalIndex = sessionData.findIndex(s => s.uniqueId === session.uniqueId);
              const sessionNumber = sessionData.length - originalIndex;
              
              return (
                <div key={session.uniqueId} className="relative">
                  {/* Timeline dot */}
                  <div className="absolute -left-[22px] sm:-left-[26px] top-1 h-4 w-4 sm:h-5 sm:w-5 rounded-full border-2 border-white bg-[#4B61FF] flex justify-center items-center z-10">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 bg-[#4B61FF] rounded-full"></div>
                  </div>

                  <div className="space-y-2">
                    {/* Session Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-8">
                        <span className="text-xs sm:text-[14px] font-semibold text-[#4B61FF] font-poppins">
                          {session.appointmentId || 'N/A'}
                        </span>
                        <span className="text-xs sm:text-[14px] font-semibold text-[#171717] font-poppins">
                          {formatApiDate(session)}
                        </span>
                      </div>
                    </div>

                    {/* Session Card */}
                    <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm p-2 sm:p-3">
                      {/* Main Content Row */}
                      <div className="flex flex-col sm:flex-row gap-3">
                        {/* Left Section - Basic Info */}
                        <div className="flex-1 flex flex-col sm:flex-row sm:justify-between gap-2 sm:gap-0">
                          <div className="flex flex-col justify-between h-full">
                            <div className="text-sm sm:text-[16px] font-medium text-[#171717] font-poppins">
                              {sessionNumber} - Consultation
                            </div>
                            <div className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                              Practitioner: {session.counsellorFirstName && session.counsellorLastName
                                ? `${session.counsellorFirstName} ${session.counsellorLastName}`.trim()
                                : session.practitioner || 'Unknown'}
                            </div>
                          </div>
                          <div className="flex flex-col sm:justify-between h-full sm:items-end w-full sm:w-[153px]">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 sm:w-3 sm:h-3 ${getStatusColor(session.status, session.currentStatus)} rounded-full`}></div>
                              <span className="text-xs sm:text-[14px] font-semibold text-[#4B61FF] font-poppins">
                                {getStatusText(session.status, session.currentStatus)}
                              </span>
                            </div>
                            <div className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                              {session.mode || "Audio"}
                            </div>
                          </div>
                        </div>

                        {/* Vertical Separator */}
                        <div className="hidden sm:block w-px h-[57px] bg-[#E2E8F0]"></div>
                        {/* Horizontal Separator for mobile */}
                        <div className="block sm:hidden w-full h-px bg-[#E2E8F0] my-3"></div>

                        {/* Right Section - Risk and Actions */}
                        <div className="flex-1 sm:min-w-[250px] space-y-3">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                            <span className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                              Overall Risk
                            </span>
                            <div
                              className="px-2.5 py-0.5 flex items-center justify-center rounded-full text-[11px] font-semibold uppercase tracking-[0.5px] font-poppins self-start sm:self-auto"
                              style={{
                                backgroundColor: getRiskBadgeStyle(session.riskStatus).backgroundColor,
                                color: getRiskBadgeStyle(session.riskStatus).color
                              }}
                            >
                              {getRiskBadgeStyle(session.riskStatus).text}
                            </div>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:h-[21px] sm:items-start sm:justify-between gap-3 sm:gap-0">
                            <div className="flex-1 flex items-center gap-2">
                              <FileText className="h-3 w-3 sm:h-4 sm:w-4 text-[#4B61FF]" />
                              <span className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                                Prescription
                              </span>
                              <span className={`text-xs sm:text-[14px] font-semibold font-poppins ${session.prescriptionUrl ? 'text-[#4B61FF]' : 'text-[#171717]'}`}>
                                {session.prescriptionUrl ? "Added" : "N/A"}
                              </span>
                            </div>
                            <div className="flex-1 flex items-start sm:justify-end gap-2">
                              <FileText className="h-3 w-3 sm:h-4 sm:w-4 text-[#4B61FF]" />
                              <span className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                                Notes
                              </span>
                              <span className={`text-xs sm:text-[14px] font-semibold font-poppins ${session.notesUrl ? 'text-[#4B61FF]' : 'text-[#171717]'}`}>
                                {session.notesUrl ? "Added" : "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content - Only for expanded sessions */}
                      {session.expanded && (
                        <>
                          {/* Horizontal Separator */}
                          <div className="w-full h-px bg-[#E2E8F0] my-4"></div>
                          
                          {/* Session Findings and Recommendations */}
                          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                            {/* Session Findings */}
                            <div className="flex-1 space-y-4 lg:space-y-4">
                              <div className="text-sm sm:text-[16px] font-medium text-[#171717] font-poppins">
                                Session Findings
                              </div>
                              <div className="space-y-4 lg:space-y-4">
                                {Object.entries(session.sessionFindings || {}).map(([key, value]) => {
                                  const label = SESSION_FINDINGS_LABELS[key] || 
                                    key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                                  
                                  return (
                                    <div key={key} className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                      <span className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                                        {label}
                                      </span>
                                      <span className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                                        {value || 'N/A'}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Vertical Separator */}
                            <div className="hidden lg:block w-px h-[156px] bg-[#E2E8F0]"></div>
                            {/* Horizontal Separator for mobile */}
                            <div className="block lg:hidden w-full h-px bg-[#E2E8F0] my-3"></div>

                            {/* Recommendations */}
                            <div className="flex-1 space-y-4 lg:space-y-4">
                              <div className="text-sm sm:text-[16px] font-medium text-[#171717] font-poppins">
                                Recommendations
                              </div>
                              <div className="space-y-4 lg:space-y-4">
                                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                  <span className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                                    Assessments
                                  </span>
                                  <span className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                                    {session.recommendations?.assessments || 'N/A'}
                                  </span>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                  <span className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                                    Exercises
                                  </span>
                                  <span className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                                    {session.recommendations?.exercises || 'N/A'}
                                  </span>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                  <span className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                                    Clinical Consultation
                                  </span>
                                  <span className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                                    {session.recommendations?.clinicalConsultation || 'N/A'}
                                  </span>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                  <span className="text-xs sm:text-[14px] text-[#334155] font-poppins tracking-[0.28px]">
                                    Medical Consultation
                                  </span>
                                  <span className="text-xs sm:text-[14px] text-[#171717] font-poppins tracking-[0.28px]">
                                    {session.recommendations?.medicalConsultation || 'N/A'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Bottom Horizontal Separator */}
                          <div className="w-full h-px bg-[#E2E8F0] my-4"></div>
                        </>
                      )}

                      {/* Final Horizontal Separator */}
                      <div className="w-full h-px bg-[#E2E8F0] my-4"></div>

                      {/* View More/Hide Button */}
                      <div className="flex justify-center">
                        <button 
                          onClick={() => toggleSession(session.uniqueId)}
                          className="text-xs sm:text-[14px] font-semibold text-[#2563EB] font-poppins tracking-[0.28px] border-0 hover:underline"
                          style={{ background: "transparent" }}
                        >
                          {session.expanded ? "Hide" : "View more"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
            ) : (
              // No results message
              <div className="text-center py-8">
                <div className="text-[#64748B] text-[14px] font-poppins">
                  {searchQuery.trim() ? `No sessions found for "${searchQuery.trim()}"` : 'No sessions available'}
                </div>
              </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// PropTypes for type checking
SessionHistory.propTypes = {
  sessions: PropTypes.arrayOf(
    PropTypes.shape({
      appointmentId: PropTypes.string,
      date: PropTypes.string,
      startTime: PropTypes.string,
      practitioner: PropTypes.string,
      status: PropTypes.string,
      mode: PropTypes.string,
      riskStatus: PropTypes.string,
      prescriptionUrl: PropTypes.string,
      notesUrl: PropTypes.string,
      selfHarmSymptoms: PropTypes.string,
      depressionSymptoms: PropTypes.string,
      anxietySymptoms: PropTypes.string,
      angerIssues: PropTypes.string,
      assessments: PropTypes.string,
      exercises: PropTypes.string,
      clinicalConsultation: PropTypes.bool,
      medicalConsultation: PropTypes.bool
    })
  ),
  onExport: PropTypes.func,
  onSessionUpdated: PropTypes.func,
  appointmentId: PropTypes.string
};

// Default props
SessionHistory.defaultProps = {
  sessions: [],
  onExport: null,
  onSessionUpdated: null,
  appointmentId: ''
};

export default React.memo(SessionHistory);